import { request } from "../../api";

export type BackendRange = "15m" | "1h" | "6h" | "24h" | "7d" | "30d";
export const BACKEND_RANGES: Array<{ id: BackendRange; label: string }> = [
  { id: "15m", label: "Last 15 minutes" },
  { id: "1h", label: "Last hour" },
  { id: "6h", label: "Last 6 hours" },
  { id: "24h", label: "Last 24 hours" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
];

export interface Page<T> {
  items: T[];
  nextCursor?: string;
}

export interface TraceSummary {
  traceId: string;
  spanId: string;
  name: string;
  service: string;
  environment?: string;
  route?: string;
  startMs: number;
  durationMs: number;
  status: string;
  httpStatus?: number;
  sessionId?: string;
  exceptionType?: string;
  exceptionMessage?: string;
}

export interface TraceSpan {
  traceId: string;
  spanId: string;
  parentSpanId?: string;
  name: string;
  service: string;
  kind: string;
  startMs: number;
  durationMs: number;
  status: string;
  attributes: Record<string, string>;
}

export interface TraceTree {
  traceId: string;
  requestId?: string;
  rootName: string;
  startMs: number;
  durationMs: number;
  spans: TraceSpan[];
}

export interface LogLine {
  ts: number;
  severity: string;
  severityNumber: number;
  body: string;
  service: string;
  environment?: string;
  traceId?: string;
  spanId?: string;
  sessionId?: string;
  templateId?: string;
  exceptionType?: string;
  exceptionStack?: string;
  attrs?: Record<string, string>;
}

export interface TraceDetail {
  trace: TraceTree;
  logs: LogLine[];
  sessionId?: string;
  services: string[];
}

export interface LogPattern {
  templateId: string;
  template: string;
  service: string;
  maxSeverity: number;
  lines: number;
  firstSeen: number;
  lastSeen: number;
  new: boolean;
}

export interface MetricInfo {
  name: string;
  kind: string;
  unit?: string;
  services: string[];
  series: number;
  lastSeen: number;
}

export interface MetricSeries {
  group: string;
  points: Array<[number, number]>;
}

export type MetricAgg = "sum" | "rate" | "avg" | "min" | "max" | "last" | "count" | "p50" | "p95" | "p99";

export interface ServiceStats {
  service: string;
  requests: number;
  errors: number;
  p50: number;
  p95: number;
  p99: number;
  logs: number;
  errorLogs: number;
  lastSeen: number;
}

export interface RouteStats {
  route: string;
  requests: number;
  errors: number;
  p50: number;
  p95: number;
  p99: number;
}

export interface MapEdge {
  from: string;
  to: string;
  kind: "service" | "database" | "browser";
  calls: number;
  errors: number;
  avgMs: number;
}

export type CaptureLevel = "everything" | "standard" | "critical" | "off";

export interface CaptureOverride {
  captureLevel?: CaptureLevel | "";
  severityFloor?: string;
  captureBodies?: boolean;
}

export interface RedactionRule {
  name: string;
  field?: string;
  pattern?: string;
  action?: string;
}

export interface BackendSettings {
  captureLevel: CaptureLevel;
  severityFloor?: string;
  captureBodies: boolean;
  slowThresholdMs: number;
  ignoreRoutes: string[];
  headerAllowlist: string[];
  runtimeMetrics: boolean;
  metricIntervalSec: number;
  seriesPerMetric: number;
  retentionDays?: number;
  dailyBudgetGb?: number;
  hardCapGb?: number | null;
  redaction: {
    disabledDetectors?: string[];
    fields?: string[];
    custom?: RedactionRule[];
    detectorAction?: string;
    hashUserIds?: boolean;
    phi?: boolean;
  };
  services?: Record<string, CaptureOverride>;
  environments?: Record<string, CaptureOverride>;
  boosts?: Array<{ service?: string; until: string; by?: string }>;
}

export interface BackendPlan {
  plan: string;
  includedGb: number;
  overagePerGbUsd: number | null;
  retentionDays: number;
  maxRetentionDays: number;
  metricSeries: number;
  hardCapGb: number;
  usedGb: number;
}

export interface ServerKey {
  id: string;
  projectId: string;
  name: string;
  environment?: string;
  prefix: string;
  createdBy?: string;
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
}

export interface BackendUsage {
  days: Array<{ day: string; gb: number; spans: number; logs: number; points: number; droppedGb: number }>;
  projects: Array<{ projectId: string; name: string; gb: number }>;
  plan: BackendPlan;
}

export type AlertLevel = "warning" | "error" | "critical";

/** A metric alert: opens a Signal while the metric stays past the threshold. */
export interface MetricRule {
  id: string;
  name: string;
  metric: string;
  service: string;
  aggregation: MetricAgg;
  comparator: "above" | "below";
  threshold: number;
  windowMin: number;
  severity: AlertLevel;
  enabled: boolean;
  breached: boolean;
  lastValue?: number;
  lastCheckedAt?: string;
  lastFiredAt?: string;
  createdAt: string;
}

export type MetricRuleInput = Pick<MetricRule, "name" | "metric" | "service" | "aggregation" | "comparator" | "threshold" | "windowMin" | "severity" | "enabled">;

type Params = Record<string, string | number | boolean | undefined>;

function qs(p: Params): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === "" || v === false) continue;
    u.set(k, v === true ? "1" : String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

export interface TraceQuery {
  range?: BackendRange;
  service?: string;
  route?: string;
  status?: "error" | "slow";
  linked?: boolean;
  q?: string;
}

export interface LogQuery {
  range?: BackendRange;
  service?: string;
  minSeverity?: number;
  templateId?: string;
  traceId?: string;
  q?: string;
}

export const backendApi = {
  facets: (range: BackendRange) => request<{ services: string[]; environments: string[] }>(`/v1/backend/facets${qs({ range })}`),
  traces: (f: TraceQuery & { cursor?: string; limit?: number }) => request<Page<TraceSummary>>(`/v1/backend/traces${qs({ ...f })}`),
  trace: (traceId: string) => request<TraceDetail>(`/v1/backend/traces/${encodeURIComponent(traceId)}`),
  logs: (f: LogQuery & { cursor?: string; limit?: number }) => request<Page<LogLine>>(`/v1/backend/logs${qs({ ...f })}`),
  patterns: (f: Pick<LogQuery, "range" | "service" | "minSeverity">) => request<{ items: LogPattern[] }>(`/v1/backend/logs/patterns${qs({ ...f })}`),
  metrics: (range: BackendRange) => request<{ items: MetricInfo[] }>(`/v1/backend/metrics${qs({ range })}`),
  metricQuery: (p: { range: BackendRange; name: string; agg: MetricAgg; groupBy?: string; service?: string }) =>
    request<{ series: MetricSeries[]; stepMs: number }>(`/v1/backend/metrics/query${qs(p)}`),
  services: (range: BackendRange) => request<{ items: ServiceStats[] }>(`/v1/backend/services${qs({ range })}`),
  routes: (service: string, range: BackendRange) => request<{ items: RouteStats[] }>(`/v1/backend/services/${encodeURIComponent(service)}/routes${qs({ range })}`),
  map: (range: BackendRange) => request<{ edges: MapEdge[]; nodes: string[] }>(`/v1/backend/map${qs({ range })}`),
  usage: () => request<BackendUsage>(`/v1/backend/usage`),

  settings: (projectId: string) => request<{ settings: BackendSettings; plan: BackendPlan }>(`/v1/projects/${projectId}/backend/settings`, { projectId }),
  saveSettings: (projectId: string, s: BackendSettings) =>
    request<{ settings: BackendSettings; plan: BackendPlan }>(`/v1/projects/${projectId}/backend/settings`, { method: "PUT", body: s, projectId }),
  boost: (projectId: string, body: { service?: string; minutes: number }) => request<{ settings: BackendSettings }>(`/v1/projects/${projectId}/backend/boost`, { method: "POST", body, projectId }),
  endBoost: (projectId: string, service: string) => request<{ settings: BackendSettings }>(`/v1/projects/${projectId}/backend/boost${qs({ service })}`, { method: "DELETE", projectId }),
  keys: (projectId: string) => request<{ keys: ServerKey[] }>(`/v1/projects/${projectId}/server-keys`, { projectId }),
  createKey: (projectId: string, body: { name: string; environment: string }) =>
    request<{ key: ServerKey; secret: string }>(`/v1/projects/${projectId}/server-keys`, { method: "POST", body, projectId }),
  revokeKey: (projectId: string, id: string) => request<void>(`/v1/projects/${projectId}/server-keys/${id}`, { method: "DELETE", projectId }),
  metricRules: (projectId: string) => request<{ rules: MetricRule[] }>(`/v1/projects/${projectId}/backend/metric-rules`, { projectId }),
  createMetricRule: (projectId: string, body: MetricRuleInput) => request<{ rule: MetricRule }>(`/v1/projects/${projectId}/backend/metric-rules`, { method: "POST", body, projectId }),
  updateMetricRule: (projectId: string, id: string, body: MetricRuleInput) =>
    request<{ rule: MetricRule }>(`/v1/projects/${projectId}/backend/metric-rules/${encodeURIComponent(id)}`, { method: "PUT", body, projectId }),
  deleteMetricRule: (projectId: string, id: string) => request<void>(`/v1/projects/${projectId}/backend/metric-rules/${encodeURIComponent(id)}`, { method: "DELETE", projectId }),
};

export const backendKeys = {
  all: ["backend"] as const,
  facets: (p: string | undefined, range: BackendRange) => ["backend", p, "facets", range] as const,
  traces: (p: string | undefined, f: TraceQuery) => ["backend", p, "traces", f] as const,
  trace: (p: string | undefined, id: string) => ["backend", p, "trace", id] as const,
  logs: (p: string | undefined, f: LogQuery) => ["backend", p, "logs", f] as const,
  patterns: (p: string | undefined, f: object) => ["backend", p, "patterns", f] as const,
  metrics: (p: string | undefined, range: BackendRange) => ["backend", p, "metrics", range] as const,
  metricQuery: (p: string | undefined, q: object) => ["backend", p, "metric-query", q] as const,
  services: (p: string | undefined, range: BackendRange) => ["backend", p, "services", range] as const,
  routes: (p: string | undefined, service: string, range: BackendRange) => ["backend", p, "routes", service, range] as const,
  map: (p: string | undefined, range: BackendRange) => ["backend", p, "map", range] as const,
  settings: (p: string | undefined) => ["backend", p, "settings"] as const,
  keys: (p: string | undefined) => ["backend", p, "server-keys"] as const,
  metricRules: (p: string | undefined) => ["backend", p, "metric-rules"] as const,
};
